import React from 'react';

/**
 * Error Boundary genérico — captura excepciones de componentes React y muestra
 * un mensaje legible en vez de pantalla blanca.
 *
 * Uso:
 *   <ErrorBoundary fallbackTitle="Error cargando movimientos">
 *     <ChildComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    this.setState({ info });
    if (this.props.onError) {
      try { this.props.onError(error, info); } catch (e) { console.warn('[ErrorBoundary] onError handler threw:', e?.message || e); }
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  render() {
    if (this.state.hasError) {
      const { fallbackTitle = 'Algo salió mal' } = this.props;
      return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 m-4" data-testid="error-boundary-fallback">
          <div className="flex items-start gap-3">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900">{fallbackTitle}</h3>
              <p className="text-sm text-red-700 mt-1">
                Se produjo un error en este componente. Los demás módulos de la app siguen funcionando.
              </p>
              <details className="mt-3 text-xs text-red-800">
                <summary className="cursor-pointer font-semibold">Detalles técnicos</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words bg-white border rounded p-2 max-h-64 overflow-auto font-mono">
                  {String(this.state.error?.message || this.state.error)}
                  {'\n\n'}
                  {String(this.state.error?.stack || '').slice(0, 1500)}
                </pre>
              </details>
              <button
                onClick={this.reset}
                data-testid="error-boundary-retry"
                className="mt-3 px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
